import React, { useState, useRef } from 'react';

const FileUpload = ({ apiBaseUrl = 'http://localhost:5000/api' }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const fileInputRef = useRef(null);
    
    // Handle file selection from input
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(prevFiles => [...prevFiles, ...files]);
        setUploadResults(null); // Clear previous results
    };
    
    // Handle drag over
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    
    // Handle drag leave
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };
    
    // Handle drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files);
        setSelectedFiles(prevFiles => [...prevFiles, ...files]);
        setUploadResults(null); // Clear previous results
    };
    
    // Remove file from list
    const removeFile = (index) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };
    
    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };
    
    // Get Cognito JWT token from storage
    const getAuthToken = () => {
        // Get the ID token stored by CognitoLogin component
        return localStorage.getItem('cognitoIdToken');
    };
    
    // Handle upload
    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            alert('Please select files to upload');
            return;
        }
        
        setUploading(true);
        setUploadResults(null);
        
        try {
            // Get auth token
            const token = getAuthToken();
            
            if (!token) {
                throw new Error('Not authenticated. Please log in.');
            }
            
            // Create FormData
            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('files', file);
            });
            
            // Make API request
            const response = await fetch(`${apiBaseUrl}/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            
            const data = await response.json();
            
            if (response.ok || response.status === 207) {
                // Success or partial success
                setUploadResults(data);
                
                // Clear successfully uploaded files from the selection
                if (data.results.successful.length > 0) {
                    const successfulFilenames = data.results.successful.map(f => f.original_filename);
                    setSelectedFiles(prevFiles => 
                        prevFiles.filter(f => !successfulFilenames.includes(f.name))
                    );
                }
            } else {
                // Error
                throw new Error(data.error || 'Upload failed');
            }
            
        } catch (error) {
            console.error('Upload error:', error);
            setUploadResults({
                message: error.message || 'Upload failed',
                results: {
                    successful: [],
                    failed: selectedFiles.map(f => ({
                        filename: f.name,
                        error: error.message
                    }))
                }
            });
        } finally {
            setUploading(false);
        }
    };
    
    return (
        <div style={{ padding: '2rem', background: '#f8f9fa', minHeight: '100vh' }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <h1 style={{ color: '#000', marginBottom: '2rem' }}>Upload Documents</h1>
                
                {/* Drag-and-drop area */}
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => !uploading && fileInputRef.current.click()}
                    style={{
                        border: isDragging ? '3px dashed #3498db' : '2px dashed #ddd',
                        borderRadius: '10px',
                        padding: '3rem',
                        textAlign: 'center',
                        background: isDragging ? '#f0f8ff' : 'white',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        marginBottom: '2rem',
                        opacity: uploading ? 0.6 : 1
                    }}
                >
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
                    <p style={{ fontSize: '1.2rem', color: '#000', marginBottom: '0.5rem' }}>
                        {uploading ? 'Uploading...' : 'Drag and drop files here'}
                    </p>
                    <p style={{ color: '#666', fontSize: '0.9rem' }}>
                        {uploading ? 'Please wait' : 'or click to browse'}
                    </p>
                    <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '1rem' }}>
                        Supported: PDF, Visio, PNG, JPG, TXT, CSV, Excel
                    </p>
                    
                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        accept=".pdf,.vsd,.vsdx,.png,.jpg,.jpeg,.txt,.csv,.xls,.xlsx,.doc,.docx"
                        disabled={uploading}
                    />
                </div>
                
                {/* Upload Results */}
                {uploadResults && (
                    <div style={{ 
                        background: 'white', 
                        borderRadius: '10px', 
                        padding: '1.5rem', 
                        marginBottom: '2rem',
                        border: `2px solid ${uploadResults.results.failed.length === 0 ? '#27ae60' : '#e67e22'}`
                    }}>
                        <h3 style={{ 
                            color: uploadResults.results.failed.length === 0 ? '#27ae60' : '#e67e22',
                            marginTop: 0,
                            marginBottom: '1rem'
                        }}>
                            {uploadResults.message}
                        </h3>
                        
                        {/* Successful uploads */}
                        {uploadResults.results.successful.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ color: '#27ae60', marginTop: 0 }}>
                                    ✓ Successfully uploaded ({uploadResults.results.successful.length})
                                </h4>
                                {uploadResults.results.successful.map((file, idx) => (
                                    <div key={idx} style={{
                                        padding: '0.75rem',
                                        background: '#d4edda',
                                        borderRadius: '5px',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.9rem'
                                    }}>
                                        <strong>{file.original_filename}</strong> ({formatFileSize(file.size)})
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Failed uploads */}
                        {uploadResults.results.failed.length > 0 && (
                            <div>
                                <h4 style={{ color: '#e74c3c', marginTop: 0 }}>
                                    ✗ Failed uploads ({uploadResults.results.failed.length})
                                </h4>
                                {uploadResults.results.failed.map((file, idx) => (
                                    <div key={idx} style={{
                                        padding: '0.75rem',
                                        background: '#f8d7da',
                                        borderRadius: '5px',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.9rem'
                                    }}>
                                        <strong>{file.filename}</strong>
                                        <br />
                                        <span style={{ color: '#721c24' }}>Error: {file.error}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Selected files list */}
                {selectedFiles.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '10px', padding: '1.5rem', marginBottom: '2rem' }}>
                        <h3 style={{ color: '#000', marginTop: 0, marginBottom: '1rem' }}>
                            Selected Files ({selectedFiles.length})
                        </h3>
                        
                        {selectedFiles.map((file, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem',
                                    background: '#f8f9fa',
                                    borderRadius: '5px',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '500', color: '#000' }}>{file.name}</div>
                                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                        {formatFileSize(file.size)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeFile(index)}
                                    disabled={uploading}
                                    style={{
                                        background: uploading ? '#ccc' : '#e74c3c',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        padding: '0.5rem 1rem',
                                        cursor: uploading ? 'not-allowed' : 'pointer',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        
                        <button
                            onClick={handleUpload}
                            disabled={uploading}
                            style={{
                                width: '100%',
                                background: uploading ? '#95a5a6' : '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                padding: '1rem',
                                fontSize: '1rem',
                                fontWeight: '500',
                                cursor: uploading ? 'not-allowed' : 'pointer',
                                marginTop: '1rem'
                            }}
                        >
                            {uploading ? 
                                'Uploading...' : 
                                `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`
                            }
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUpload;
